/**
 * Web role policy for admin, direct-workflow users, and master distributors.
 *
 * Reps and clinics retain their existing direct-workflow paths. Masters get explicit portal routes.
 *
 * SECURITY POSTURE: everything in this file is COSMETIC. The real control is server-side -
 * every admin endpoint keeps @Roles('admin') and every rep-open list is scoped to the requesting
 * user by the backend. Hiding a nav item or redirecting a route only keeps honest users out of
 * pages that would 403 anyway. Never treat this file as the gate.
 *
 * Both non-admin path sets are allowlists; new pages start admin-only.
 */
export const ADMIN_ROLE = 'admin';
export const SALES_REP_ROLE = 'sales_rep';
/** Clinic-site login (7/27 clinic model, TC-260727-12/-13): one clinic per account, hard
 *  tenant isolation server-side. Admin-provisioned only - never self-registered. */
export const CLINIC_ROLE = 'clinic';
export const MASTER_DISTRIBUTOR_ROLE = 'master_distributor';

/** Who may log into the web portal at all. (Mobile still admits only admin+sales_rep - the
 *  clinic role is web-first until the mobile follow-up lands.) */
export const WEB_LOGIN_ROLES = [ADMIN_ROLE, SALES_REP_ROLE, CLINIC_ROLE, MASTER_DISTRIBUTOR_ROLE];

/** The only paths a NON-ADMIN (sales_rep or clinic login) may visit; everything else
 *  redirects to REP_HOME. The server scopes what each page shows per tenant.
 *
 *  '/my-hub' is rep-facing by design: the server resolves the caller's own clinics (and, on its
 *  Team tab, the reps granted to the caller) - there is no id to pass and nothing to widen. A
 *  clinic login gets its own clinic, read-only. '/my-team' is the page's old path and stays here
 *  so its redirect to /my-hub is not admin-guarded (a rep's bookmark must still work). */
export const REP_ALLOWED_PATHS = [
  '/orders',
  '/ivr-details',
  '/invoices',
  '/my-hub',
  '/my-team',
  '/settings',
  '/help-center',
  '/resources',
];

/** Where a rep lands: their orders. The admin Dashboard calls admin-only report endpoints (403). */
export const REP_HOME = '/orders';
export const DISTRIBUTOR_HOME = '/distributor';
export const DISTRIBUTOR_CLINIC_SHIPMENTS = '/distributor-clinic/shipments';

export const isAdmin = (user) => user?.role === ADMIN_ROLE;
export const isDistributorAdmin = (user) => isAdmin(user) && user.adminWorkspace === 'distributor';
export const DISTRIBUTOR_ADMIN_HOME = '/admin/distributor-overview';
export const distributorAdminMayVisit = (path) =>
  [
    DISTRIBUTOR_ADMIN_HOME,
    '/admin/distributors',
    '/admin/distributor-customers',
    '/admin/distributor-orders',
    '/admin/distributor-ivrs',
    '/admin/distributor-shipments',
    '/admin/distributor-bank',
    '/admin/distributor-payment-test',
    '/admin/distributor-resources',
    '/admin/distributor-reports',
    '/settings',
  ].includes(path);

export const canLogInToWeb = (role) => WEB_LOGIN_ROLES.includes(role);

export const repMayVisit = (path) =>
  REP_ALLOWED_PATHS.some((allowed) => path === allowed || path.startsWith(`${allowed}/`));

export const distributorMayVisit = (path) =>
  [
    DISTRIBUTOR_HOME,
    '/distributor/customers',
    '/distributor/orders',
    '/distributor/ivrs',
    '/distributor/shipments',
    '/distributor/bank',
    '/distributor/settings',
    '/distributor/resources',
    '/distributor/help',
    '/distributor/reports',
  ].includes(path);

export const homeForRole = (user) =>
  isDistributorAdmin(user)
    ? DISTRIBUTOR_ADMIN_HOME
    : user?.role === MASTER_DISTRIBUTOR_ROLE
      ? DISTRIBUTOR_HOME
      : user?.role === ADMIN_ROLE
        ? '/'
        : REP_HOME;

export const mayVisit = (user, path) =>
  isDistributorAdmin(user)
    ? distributorAdminMayVisit(path)
    : user?.role === MASTER_DISTRIBUTOR_ROLE
      ? distributorMayVisit(path)
      : path === DISTRIBUTOR_CLINIC_SHIPMENTS
        ? user?.role === CLINIC_ROLE && Boolean(user?.distributor)
        : user?.role === ADMIN_ROLE
          ? path !== DISTRIBUTOR_HOME && !path.startsWith(`${DISTRIBUTOR_HOME}/`)
          : repMayVisit(path);
